import React, { useState } from 'react';
import { Alert, Button, Grid, LinearProgress } from '@mui/material';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { ANALYTICS_WINDOWS, AnalyticsWindow } from '../model';
import useAnalyticsSummary from '../hooks/useAnalyticsSummary';
import WindowTiles from '../components/WindowTiles';
import InsightCards from '../components/InsightCards';
import { DailyActiveChart, ModuleUsageChart } from '../components/UsageCharts';
import PagesTable from '../components/PagesTable';

const AnalyticsPage: React.FC = () => {
  const [selectedWindow, setSelectedWindow] = useState<AnalyticsWindow>('7d');
  const { data, loading, error, retry } = useAnalyticsSummary(selectedWindow);
  // Content follows the data's window, not the clicked tile, so nothing mislabels the previous
  // window's numbers while the new ones load.
  const shownWindow = data?.window ?? selectedWindow;
  const windowLabel = ANALYTICS_WINDOWS.find((option) => option.key === shownWindow)?.label ?? '';
  // A one-day window is a single bar, so the daily chart only appears for multi-day windows.
  const showDailyChart = shownWindow !== 'today';

  return (
    <Grid container spacing={2} style={{ padding: '1%' }}>
      <Grid item xs={12}>
        <WindowTiles tiles={data?.tiles} selected={selectedWindow} onSelect={setSelectedWindow} />
      </Grid>
      {loading && (
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      )}
      {error && (
        <Grid item xs={12}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={retry}>
                Retry
              </Button>
            }
          >
            Could not load analytics.
          </Alert>
        </Grid>
      )}
      {data && !error && (
        <>
          <Grid item xs={12}>
            <InsightCards pages={data.pages} idleUsers={data.idle_users} windowLabel={windowLabel} />
          </Grid>
          {showDailyChart && (
            <Grid item xs={12} md={7}>
              <DailyActiveChart days={data.daily_active_users} />
            </Grid>
          )}
          <Grid item xs={12} md={showDailyChart ? 5 : 12}>
            <ModuleUsageChart modules={data.modules} />
          </Grid>
          <Grid item xs={12}>
            <PagesTable pages={data.pages} />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default AnalyticsPage;
